import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import PropTypes from 'prop-types';

import PersonIcon from '@mui/icons-material/Person';
import CloseIcon from '@mui/icons-material/Close';
import {
  Autocomplete,
  Box,
  Button,
  Divider,
  Fade,
  IconButton,
  LinearProgress,
  Modal,
  TextField,
  Typography,
  Backdrop,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
// import expensesForm from 'components/Forms/expensesForm';
import { CustomerForm } from 'components/Forms';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CircularProgress from '@mui/material/CircularProgress';
import dayjs from 'dayjs';
import Loading from 'components/Loading';
import _ from 'lodash';
import ExpensesTable from '../../components/Table/ExpensesTable';
import EditExpensesModal from '../../components/Modal/EditExpensesModal';
import ExpensesApplyForm from '../../components/Forms/ExpensesApplyForm';
import FilterPersornalExpenses from '../../components/Card/FilterPersornalExpenses';
import ImageUpload from '../../components/ImageUpload/ImageUpload';
import FileUpload from '../../components/FileUpload/FileUpload';
import { useSocket } from '../../contexts/SocketContext';
import * as actions from '../../redux/actions';
import api from '../../utils/functions/api';
import { showAlert, confirmAsync } from '../../utils/functions/globalDialog';
import { formatEmployeeThaiName } from '../../utils/functions/employeeName';
import resizeImageFileIfNeeded from '../../utils/functions/resizeImageFile';

// ชื่อเดือนภาษาไทย (index 0 = มกราคม) — ใช้สร้างข้อความ "รายการ" อัตโนมัติของการเบิก CRL
const THAI_MONTHS = [
  'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
  'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม',
];

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
};

// crlOnly=true → หน้า "ตั้งเบิก CRL" (เมนูแยกใต้ Profile): ล็อกโหมดเบิกเงินเดือนล่วงหน้าเสมอ
// ไม่มี checkbox, ตัดส่วนแนบรูป, ตั้งเบิกได้ทุกวัน (งวดที่จะหัก backend คิดจากวันที่ขอ — ดู insert() ใน expenses.service.js)
export default function PersonalExpenses({ title, subtitle, crlOnly = false }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const expenses = useSelector((state) => state.expenses);
  const projects = useSelector((state) => state.project);
  const budget = useSelector((state) => state.budget);
  const me = useSelector((state) => state.me);
  const customer = useSelector((state) => state.customer);
  const customerType = useSelector((state) => state.customerType);

  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);
  const [open, setOpen] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editExpenseId, setEditExpenseId] = useState(null);
  const [date, setDate] = useState(dayjs().add(1, 'day').toDate());
  // ค่าเริ่มต้น "ประเภทการเบิก" = เบิกเพื่อจ่าย (PAY) — ป้อนเป็น defaultValue ของช่อง expenses_type ในฟอร์ม
  // (dropdown มีแค่ PAY/REFUND) เดิมเป็น 'TRANSFER' ซึ่งไม่ match option ไหนเลย ทำให้ record ที่สร้างมี
  // expenses_type='TRANSFER' แล้ว chip ในหน้าอนุมัติ (ExpensesTableApplove) โชว์ "-" เพราะรู้จักแค่ PAY/REFUND
  const [paidType, setPaidType] = useState('PAY');
  const [projectSelect, setProjectSelect] = useState('');
  const [budgetSelect, setBudgetSelect] = useState('');
  const [findProject, setFindProject] = useState();
  const [search, setSearch] = useState('');
  const [bank, setBank] = useState();
  const [openCustomerModal, setOpenCustomerModal] = useState(false);
  const [customerSubmitting, setCustomerSubmitting] = useState(false);
  const [customerSelect, setCustomerSelect] = useState('');
  const [addedImage, setAddedImage] = useState([]);

  // "เบิกเงินเดือนล่วงหน้า (CRL)" — ติ๊กแล้วเลือกพนักงานที่จะเบิกแทน (default = ตัวเอง)
  // โหมด crlOnly → เริ่ม true เสมอ (ไม่มี checkbox ให้ปลด)
  const [isSalaryAdvance, setIsSalaryAdvance] = useState(crlOnly);
  const [salaryAdvanceEmployee, setSalaryAdvanceEmployee] = useState(null);
  const [salaryAdvanceOptions, setSalaryAdvanceOptions] = useState([]);
  const [salaryAdvanceLoading, setSalaryAdvanceLoading] = useState(false);
  // { employeeType: 'DAILY'|'FULLTIME', monthlyWithdrawCount } ของพนักงานที่เลือก — ใช้สร้างข้อความ "รายการ"
  const [crlNameInfo, setCrlNameInfo] = useState(null);

  // Socket.IO real-time listeners
  const socket = useSocket();

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setValue,
    getValues,
    watch,
  } = useForm();

  // ข้อความ "รายการ" ที่ระบบ auto กรอกให้ล่าสุด (ของการเบิก CRL) — เก็บไว้เทียบว่าผู้ใช้แก้เองหรือยัง
  const crlAutoNameRef = useRef('');
  const baseAmount = watch('base_amount');

  const {
    control: customerControl,
    formState: { errors: customerErrors },
    handleSubmit: handleCustomerSubmit,
    reset: resetCustomer,
    setValue: setCustomerValue,
  } = useForm();

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    if (isSubmitting) return;
    setOpen(false);
    setSubmitError(null);
  };

  const handleOpenCustomerModal = () => setOpenCustomerModal(true);
  const handleCloseCustomerModal = () => {
    setOpenCustomerModal(false);
    resetCustomer();
  };

  const eachBudget = _.find(budget?.rows, { _id: budgetSelect?._id });

  const fetchExpensesPage = useCallback(() => {
    if (!me?.userData?.id) return Promise.resolve();
    return dispatch(
      actions.expensesAll({
        me: me.userData.id,
        search,
        findProject: findProject?._id,
        page,
        size,
      }),
    );
  }, [dispatch, me?.userData?.id, search, findProject?._id, page, size]);

  useEffect(() => {
    dispatch(actions.meGet());

    return () => {};
  }, []);

  useEffect(() => {
    dispatch(actions.customerAll({}));
    dispatch(actions.customerTypeAll({}));
  }, []);

  useEffect(() => {
    fetchExpensesPage();
  }, [fetchExpensesPage]);

  useEffect(() => {
    dispatch(actions.projectAll({ lite: true }));
  }, []);

  // Socket.IO listeners for real-time updates
  useEffect(() => {
    if (!socket) return;

    const refresh = () => {
      fetchExpensesPage();
    };

    socket.on('expenses:created', refresh);
    socket.on('expenses:updated', refresh);
    socket.on('expenses:deleted', refresh);

    return () => {
      socket.off('expenses:created', refresh);
      socket.off('expenses:updated', refresh);
      socket.off('expenses:deleted', refresh);
    };
  }, [socket, fetchExpensesPage]);

  useEffect(() => {
    dispatch(actions.budgetReset());
    if (projectSelect?._id) {
      dispatch(actions.budgetAll({ project_id: projectSelect._id }));
    }
  }, [projectSelect]);

  // ช่วงวันที่ใช้เช็ค timestamp (รายชื่อพนักงานใน dropdown + auto เลือกโครงการ) — ใช้ครึ่งเดือนเดียวกับ
  // งวดที่จะไปหัก (getCrlDailyHalf): วันที่กดเบิก (วันนี้) 1–15 → 1–15, 16–สิ้นเดือน → 16–สิ้นเดือน
  // ไม่ดู "วันที่" ในฟอร์ม (default = พรุ่งนี้ ทำให้เคยได้ช่วงไม่ตรงกับงวดที่หักจริง)
  const getSalaryAdvancePeriodRange = () => {
    const now = dayjs();
    const firstHalf = now.date() <= 15;
    return {
      dateStart: (firstHalf ? now.date(1) : now.date(16)).startOf('day').toISOString(),
      dateEnd: (firstHalf ? now.date(15).endOf('day') : now.endOf('month')).toISOString(),
    };
  };

  // โหลดรายชื่อพนักงานที่เลือกได้ในดรอปดาวน์ "เบิกเงินเดือนล่วงหน้า" — เฉพาะคนที่มี timestamp (ลงเวลา)
  // ในงวดปัจจุบันจริงเท่านั้น
  // ไม่ยัดตัวเองเข้าไปถ้าไม่มีการลงเวลา เพราะเบิก CRL ต้องมีการลงเวลาในงวดก่อน (backend ก็บังคับซ้ำอีกชั้น)
  useEffect(() => {
    if (!isSalaryAdvance) return undefined;
    let cancelled = false;
    const fetchOptions = async () => {
      setSalaryAdvanceLoading(true);
      try {
        const { dateStart, dateEnd } = getSalaryAdvancePeriodRange();
        const { data } = await api.get(
          `${process.env.REACT_APP_API_URL}/timestamp/employees-in-range`,
          { params: { dateStart, dateEnd } },
        );
        if (cancelled) return;
        const opts = _.uniqBy(data || [], (e) => String(e?._id || e?.id));
        setSalaryAdvanceOptions(opts);
        // ถ้าคนที่เลือกไว้ไม่อยู่ในลิสต์ใหม่ (ตัวเองไม่มี timestamp / เปลี่ยนวันที่ข้ามงวด) — ล้างการเลือก
        setSalaryAdvanceEmployee((prev) => {
          if (!prev) return prev;
          const stillValid = opts.some(
            (o) => String(o?._id || o?.id) === String(prev?._id || prev?.id),
          );
          return stillValid ? prev : null;
        });
      } catch (error) {
        if (!cancelled) {
          setSalaryAdvanceOptions([]);
          setSalaryAdvanceEmployee(null);
        }
      } finally {
        if (!cancelled) setSalaryAdvanceLoading(false);
      }
    };
    fetchOptions();
    return () => {
      cancelled = true;
    };
  }, [isSalaryAdvance, me?.userData?.id]);

  // โหมด crlOnly: เปิดฟอร์มมาก็ล็อกโหมด CRL + default พนักงาน = ตัวเอง + ประเภทการเบิก = เบิกเพื่อจ่าย
  // (เดิม logic นี้อยู่ใน handleToggleSalaryAdvance ตอนติ๊ก checkbox — checkbox ถูกถอดออกแล้ว)
  useEffect(() => {
    if (!crlOnly) return;
    setIsSalaryAdvance(true);
    setPaidType('PAY');
    setValue('expenses_type', 'PAY');
    setSalaryAdvanceEmployee((cur) => cur || me?.userData || null);
  }, [crlOnly, me?.userData?.id]);

  // เลือก/เปลี่ยนพนักงานในช่อง CRL → auto เลือก "โครงการที่พนักงานคนนั้นลงเวลามากที่สุดในงวด"
  // + งบประมาณที่ชื่อมีคำว่า "ค่าแรง" ของโครงการนั้น (ยังแก้เองได้ภายหลัง)
  // เป็น effect (ไม่ใช่เรียกจาก onChange) เพื่อให้มี cleanup กัน race: สลับพนักงาน A→B เร็ว ๆ แล้ว
  // response ของ A มาช้ากว่า B จะไม่เขียนทับค่าของ B (ค่าที่ auto จะ "ค้าง" เป็นของคนก่อนหน้า)
  useEffect(() => {
    if (!isSalaryAdvance) return undefined;
    const empId = salaryAdvanceEmployee?._id || salaryAdvanceEmployee?.id;
    if (!empId) {
      // ยังไม่เลือกพนักงาน หรือคนที่เลือกหลุดจากงวด (reconcile ล้างให้) → ล้าง auto field ตามไปด้วย
      setProjectSelect('');
      setValue('project', null);
      setBudgetSelect('');
      setValue('budget', null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const { dateStart, dateEnd } = getSalaryAdvancePeriodRange();
        const { data: topProject } = await api.get(
          `${process.env.REACT_APP_API_URL}/timestamp/employee-top-project`,
          { params: { employeeId: empId, dateStart, dateEnd } },
        );
        if (cancelled) return;
        if (!topProject?._id) {
          // พนักงานคนใหม่ไม่มีโครงการที่ลงเวลาในงวด → ล้างโครงการ/งบของคนก่อนหน้าทิ้ง ไม่ให้ค้าง
          setProjectSelect('');
          setValue('project', null);
          setBudgetSelect('');
          setValue('budget', null);
          return;
        }
        const projectRow =
          _.find(projects?.rows, (p) => String(p._id) === String(topProject._id)) ||
          topProject;
        setProjectSelect(projectRow);
        setValue('project', projectRow._id);
        setBudgetSelect('');
        setValue('budget', null);

        const { data: budgetData } = await api.get(
          `${process.env.REACT_APP_API_URL}/budget`,
          { params: { project_id: topProject._id, size: 999999 } },
        );
        if (cancelled) return;
        const laborBudget = (budgetData?.rows || [])
          .filter((b) => String(b.project_id) === String(topProject._id))
          .find((b) => (b.name || '').includes('ค่าแรง'));
        if (laborBudget) {
          setBudgetSelect(laborBudget);
          setValue('budget', laborBudget._id);
        }
      } catch (err) {
        // เงียบ — ถ้า auto ไม่สำเร็จ ให้ผู้ใช้เลือกโครงการ/งบเอง
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isSalaryAdvance, salaryAdvanceEmployee]);

  // โหลดข้อมูลประเภทพนักงาน (รายวัน/รายเดือน) + จำนวนครั้งที่เบิก CRL ไปแล้วในเดือนนี้ ของคนที่เลือก
  useEffect(() => {
    if (!isSalaryAdvance || !salaryAdvanceEmployee) {
      setCrlNameInfo(null);
      return undefined;
    }
    // เคลียร์ข้อมูลของพนักงานคนก่อนหน้าก่อน แล้วค่อยโหลดใหม่ — name effect จะรอจน crlNameInfo ใหม่มาถึง
    // ค่อยกรอกช่อง "รายการ" (กันข้อความผสมระหว่างเลข/ประเภทของคนเดิมกับชื่อคนใหม่)
    setCrlNameInfo(null);
    let cancelled = false;
    const empId = salaryAdvanceEmployee._id || salaryAdvanceEmployee.id;
    api
      .get(`${process.env.REACT_APP_API_URL}/expenses/crl-name-info`, {
        params: { employeeId: empId },
      })
      .then(({ data }) => {
        if (!cancelled) setCrlNameInfo(data || null);
      })
      .catch(() => {
        if (!cancelled) setCrlNameInfo(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isSalaryAdvance, salaryAdvanceEmployee]);

  // CRL: กรอกช่อง "รายการ" ให้อัตโนมัติตาม pattern มาตรฐาน — แยกคำตามประเภทพนักงาน:
  //   รายเดือน (FULLTIME): "ขอเบิก CRL เงินล่วงหน้า ครั้งที่ <N> ประจำเดือน <เดือนไทย> <พ.ศ.> (<ชื่อ>) จำนวน <ยอด> บาท"
  //     N = จำนวนครั้งที่เบิก CRL ไปแล้วในเดือนนี้ + 1 (นับต่อพนักงาน จาก backend)
  //   รายวัน (DAILY): "ขอเบิก CRL เงินล่วงหน้า งวดที่ <1|2> วันที่ <ช่วงวัน> <เดือนไทย> <พ.ศ.> (<ชื่อ>) จำนวน <ยอด> บาท"
  //     งวดที่ 1 = วันที่ 1-15, งวดที่ 2 = วันที่ 16-<วันสุดท้ายของเดือน> (อิง "วันที่" ที่กดเบิก)
  // เดือน/ปี = ปัจจุบันเสมอ, ยอดมาจากช่อง "รวมเป็นเงิน (ก่อน VAT)" (เติม "จำนวน ... บาท" เมื่อกรอกยอดแล้ว)
  // หยุด auto ทันทีถ้าผู้ใช้แก้ข้อความในช่องเอง (เทียบกับ crlAutoNameRef)
  useEffect(() => {
    if (!isSalaryAdvance || !salaryAdvanceEmployee || !crlNameInfo) return;
    const currentName = getValues('name') || '';
    if (currentName && currentName !== crlAutoNameRef.current) return;
    const isDaily = crlNameInfo.employeeType === 'DAILY';
    const now = dayjs();
    const monthYear = `${THAI_MONTHS[now.month()]} ${now.year() + 543}`;
    const amount = parseFloat(baseAmount);
    const amountClause = Number.isFinite(amount) && amount > 0
      ? ` จำนวน ${amount.toLocaleString('en-US')} บาท`
      : '';

    let periodPart;
    if (isDaily) {
      // งวด = ครึ่งเดือนที่จะไปหัก — โหมด crlOnly ดูจากวันที่กดเบิก (วันนี้), หน้าเบิกปกติดูจาก "วันที่" ในฟอร์ม
      const half = crlOnly
        ? getCrlDailyHalf()
        : (dayjs(date).date() <= 15 ? 1 : 2);
      const dayRange = half === 1 ? '1-15' : `16-${now.daysInMonth()}`;
      periodPart = `งวดที่ ${half} วันที่ ${dayRange} ${monthYear}`;
    } else {
      const seqNo = (crlNameInfo.monthlyWithdrawCount || 0) + 1;
      periodPart = `ครั้งที่ ${seqNo} ประจำเดือน ${monthYear}`;
    }

    const text = `ขอเบิก CRL เงินล่วงหน้า ${periodPart} `
      + `(${formatEmployeeThaiName(salaryAdvanceEmployee)})${amountClause}`;
    if (text !== currentName) {
      setValue('name', text);
      crlAutoNameRef.current = text;
    }
  }, [isSalaryAdvance, salaryAdvanceEmployee, crlNameInfo, crlOnly, date, baseAmount, getValues, setValue]);

  // แยกรายการตามหน้าที่สร้าง — แต่ละหน้าโชว์เฉพาะของตัวเอง: CRL (is_salary_advance) โชว์แค่
  // /profile/crl-disbursement, รายการเบิกน้ำมัน (ชื่อมีคำว่า "น้ำมัน" หรือมี fuel_station+fuel_liters
  // ที่เซ็ตเฉพาะตอนสร้างจากฟอร์มเติมน้ำมัน) โชว์แค่ /profile/fuel-setting, ที่เหลือถึงโชว์ที่นี่
  const isFuelExpense = (e) => Boolean(e?.name?.includes('น้ำมัน'))
    || (e?.fuel_station != null && e?.fuel_liters != null);
  const filter = _.filter(
    expenses?.rows,
    (e) => e?.employee?._id === me?.userData?.id
      && (crlOnly ? e?.is_salary_advance === true : e?.is_salary_advance !== true)
      && !isFuelExpense(e),
  );

  const handleDelete = async (id) => {
    const confirm = await confirmAsync('ยกเลิกรายการ');
    if (confirm) {
      try {
        await dispatch(actions.expensesPut(id, {
          status: 'CANCEL',
          approval_actor: me?.userData?._id,
          approval_actor_name: formatEmployeeThaiName(me?.userData),
        }));
        fetchExpensesPage();
      } catch (error) {}
    }
  };

  const onSubmitCustomer = async (data) => {
    if (customerSubmitting) return;
    setCustomerSubmitting(true);
    try {
      data.bank = {
        bank_name: bank,
        account_name: data?.bank?.account_name,
        account_number: data?.bank?.account_number,
      };
      await dispatch(actions.customerCreate(data));
      await dispatch(actions.customerAll({}));
      handleCloseCustomerModal();
    } catch (error) {
      // handle error
    } finally {
      setCustomerSubmitting(false);
    }
  };

  // Pass to PaymentForm for "Add Customer" from Autocomplete
  const handleAddCustomerFromPayment = () => {
    handleOpenCustomerModal();
  };

  const renderCustomerModal = () => (
    <Modal
      open={openCustomerModal}
      onClose={handleCloseCustomerModal}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 300 }}
    >
      <Fade in={openCustomerModal}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95%', sm: '80%', md: 700 },
            maxHeight: '90vh',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 0,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              p: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PersonIcon sx={{ mr: 1 }} />
              <Typography variant="h6">เพิ่มลูกค้าใหม่</Typography>
            </Box>
            <IconButton
              size="small"
              onClick={handleCloseCustomerModal}
              sx={{ color: 'white' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ p: 3, maxHeight: 'calc(90vh - 64px)', overflowY: 'auto' }}>
            <form onSubmit={handleCustomerSubmit(onSubmitCustomer)}>
              <CustomerForm
                Controller={Controller}
                control={customerControl}
                errors={customerErrors}
                customerType={customerType}
                bank={bank}
                setBank={setBank}
              />

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  mt: 3,
                  gap: 1,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={handleCloseCustomerModal}
                  startIcon={<CloseIcon />}
                  disabled={customerSubmitting}
                >
                  ยกเลิก
                </Button>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={customerSubmitting}
                  startIcon={customerSubmitting ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
                >
                  {customerSubmitting ? 'กำลังบันทึก...' : 'บันทึกข้อมูล'}
                </Button>
              </Box>
            </form>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );

  const handleEdit = (id) => {
    setEditExpenseId(id);
    setEditModalOpen(true);
  };

  const onSubmit = async (data) => {
    if (!projectSelect) {
      showAlert('กรุณาเลือกโครงการ');
      return;
    }
    if (!budgetSelect) {
      showAlert('กรุณาเลือกงบประมาณ');
      return;
    }
    if (isSalaryAdvance && !salaryAdvanceLoading && salaryAdvanceOptions.length === 0) {
      showAlert('ไม่มีพนักงานที่ลงเวลาในงวดนี้ จึงเบิกเงินเดือนล่วงหน้าไม่ได้');
      return;
    }
    if (isSalaryAdvance && !salaryAdvanceEmployee) {
      showAlert('กรุณาเลือกพนักงานที่เบิกเงินเดือนล่วงหน้า');
      return;
    }

    const confirm = await confirmAsync('บันทึกข้อมูล');
    if (!confirm) return;

    setIsSubmitting(true);
    setSubmitError(null);
    setUploadProgress(0);
    try {
      // อัปโหลดรูปบิลทีละไฟล์ (multipart) ก่อน แทนที่จะยัด base64 ทุกรูปรวมกัน
      // เข้า JSON เดียว — กัน 413 Payload Too Large ตอนแนบรูปหลายรูป/ไฟล์ใหญ่
      // resize ให้เสร็จก่อนทุกไฟล์ เพื่อรู้ total bytes จริงที่จะอัป แล้วค่อยอัป
      // พร้อมรายงาน % ตามน้ำหนักไฟล์ ให้ user เห็นว่าเหลืออีกนานแค่ไหน
      const filesToUpload = await Promise.all(
        (addedImage || []).map(async (img) => ({
          file: await resizeImageFileIfNeeded(img.file),
          name: img.name,
        })),
      );
      const totalBytes = filesToUpload.reduce((sum, f) => sum + f.file.size, 0) || 1;
      const loadedBytes = new Array(filesToUpload.length).fill(0);
      const reportProgress = () => {
        const loaded = loadedBytes.reduce((a, b) => a + b, 0);
        setUploadProgress(Math.min(99, Math.round((loaded / totalBytes) * 100)));
      };

      const uploadedImageIds = await Promise.all(
        filesToUpload.map(async ({ file, name }, idx) => {
          const formData = new FormData();
          formData.append('file', file, name);
          const { data } = await api.post(
            `${process.env.REACT_APP_API_URL}/expenses/upload-image`,
            formData,
            {
              headers: { 'Content-Type': 'multipart/form-data' },
              onUploadProgress: (evt) => {
                loadedBytes[idx] = evt.loaded;
                reportProgress();
              },
            },
          );
          return data.id;
        }),
      );
      setUploadProgress(100);

      const dataSubmit = {
        ...data,
        paidType,
        date: dayjs(date).endOf('day').toDate(),
        employee: me?.userData?._id,
        employee_data: me?.userData
          ? {
              title: me.userData.title,
              firstname: me.userData.firstname,
              lastname: me.userData.lastname,
              firstname_th: me.userData.firstname_th,
              lastname_th: me.userData.lastname_th,
            }
          : undefined,
        budgetSelect: eachBudget
          ? { name: eachBudget.name, remain: eachBudget.remain }
          : eachBudget,
        projectSelect: projectSelect
          ? {
              project_number: projectSelect.project_number,
              name: projectSelect.name,
            }
          : projectSelect,
        customer: customerSelect,
        bill_images: uploadedImageIds.filter(Boolean),
        is_salary_advance: isSalaryAdvance,
        salary_advance_employee: isSalaryAdvance
          ? salaryAdvanceEmployee?._id || salaryAdvanceEmployee?.id || null
          : null,
        // งวดที่จะหัก (salary_advance_period_*) backend คำนวณเองจากวันที่ขอ — ไม่ต้องส่งไป
      };
      await dispatch(actions.expensesCreate(dataSubmit));
      reset();
      // reset() ล้าง expenses_type เป็น undefined — เซ็ตกลับเป็น PAY (ค่าเริ่มต้น) ทันที กันรอบสร้างถัดไป
      // ส่งค่าว่าง ถ้าผู้ใช้ไม่ได้แตะ dropdown
      setValue('expenses_type', 'PAY');
      setOpen(false);
      setSubmitError(null);
      setAddedImage([]);
      setProjectSelect('');
      setBudgetSelect('');
      setDate(dayjs().add(1, 'day').toDate());
      setPaidType('PAY');
      setCustomerSelect('');
      setIsSalaryAdvance(crlOnly); // โหมด CRL คงโหมดไว้ (ไม่มี checkbox ให้ติ๊กกลับ)
      setSalaryAdvanceEmployee(crlOnly ? me?.userData || null : null);
      setCrlNameInfo(null);
      crlAutoNameRef.current = '';
    } catch (err) {
      console.error('expensesCreate failed:', err);
      setSubmitError('บันทึกไม่สำเร็จ กรุณาลองใหม่อีกครั้ง');
    } finally {
      setIsSubmitting(false);
      setUploadProgress(0);
    }
  };

  const renderModal = () => (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95%', sm: '80%', md: 700 },
          maxHeight: '90vh',
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden', // container hides overflow, body will scroll
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography id="modal-modal-title" variant="h6" component="h2">
            <b className="py-2">{crlOnly ? 'ตั้งเบิก CRL (เบิกเงินเดือนล่วงหน้า)' : 'รายการเบิกจ่าย'}</b>
          </Typography>
          <IconButton size="small" onClick={handleClose} aria-label="ปิด">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Body (scrollable) */}
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            overflowY: 'auto',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ flex: 1 }}>
            <ExpensesApplyForm
              control={control}
              Controller={Controller}
              date={date}
              setDate={setDate}
              paidType={paidType}
              setPaidType={setPaidType}
              projects={projects}
              setProjectSelect={setProjectSelect}
              projectSelect={projectSelect}
              setValue={setValue}
              budgets={budget}
              budgetSelect={budgetSelect}
              setBudgetSelect={setBudgetSelect}
              bank={bank}
              setBank={setBank}
              customer={customer}
              onAddCustomer={handleAddCustomerFromPayment}
              setCustomerSelect={setCustomerSelect}
              personalMode={true}
              isSalaryAdvance={isSalaryAdvance}
              watch={watch}
              topSlot={!crlOnly ? null : (
                /* โหมดตั้งเบิก CRL — ไม่มี checkbox (ล็อกโหมดเสมอ) เหลือแค่ช่องเลือกพนักงาน */
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    flexWrap: 'wrap',
                  }}
                >
                  {isSalaryAdvance && (
                    <Autocomplete
                      options={salaryAdvanceOptions}
                      value={salaryAdvanceEmployee}
                      loading={salaryAdvanceLoading}
                      noOptionsText="ไม่พบพนักงานที่ลงเวลาในงวดนี้"
                      getOptionLabel={(o) => formatEmployeeThaiName(o)}
                      isOptionEqualToValue={(o, v) =>
                        String(o?._id || o?.id) === String(v?._id || v?.id)
                      }
                      onChange={(e, newValue) => setSalaryAdvanceEmployee(newValue)}
                      sx={{ flex: 1, minWidth: 240 }}
                      renderInput={(params) => {
                        const noEligible =
                          !salaryAdvanceLoading && salaryAdvanceOptions.length === 0;
                        return (
                          <TextField
                            {...params}
                            size="small"
                            fullWidth
                            label="พนักงานที่เบิกเงินเดือนล่วงหน้า"
                            error={noEligible}
                            helperText={
                              noEligible
                                ? 'ไม่มีพนักงานที่ลงเวลาในงวดนี้ — เบิกเงินเดือนล่วงหน้าไม่ได้'
                                : ' '
                            }
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {salaryAdvanceLoading && <CircularProgress size={16} />}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        );
                      }}
                    />
                  )}
                </Box>
              )}
            />

            {/* Bill Image Upload Section — ตัดออกในโหมดตั้งเบิก CRL */}
            {!crlOnly && (
            <Box sx={{ px: 3, pb: 2 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography
                variant="subtitle1"
                sx={{
                  mb: 1.5,
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                📎 แนบรูปบิล / PDF
              </Typography>
              <Box
                sx={{
                  border: '2px dashed',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 2,
                  bgcolor: 'action.hover',
                  textAlign: 'center',
                }}
              >
                <FileUpload
                  maxNumber={10}
                  files={addedImage}
                  setFiles={setAddedImage}
                  title={'เลือกรูปบิล / PDF'}
                  accept="image/*,.pdf"
                />
                {addedImage.length === 0 && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1, display: 'block' }}
                  >
                    รองรับสูงสุด 10 ไฟล์
                  </Typography>
                )}
              </Box>
            </Box>
            )}
          </Box>

          {/* Sticky Submit Footer */}
          <Box
            sx={{
              p: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            {submitError && (
              <Typography variant="body2" color="error" sx={{ mb: 1, textAlign: 'right' }}>
                {submitError}
              </Typography>
            )}
            {isSubmitting && addedImage?.length > 0 && (
              <Box sx={{ mb: 1 }}>
                <LinearProgress
                  variant={uploadProgress < 100 ? 'determinate' : 'indeterminate'}
                  value={uploadProgress}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'right', mt: 0.3 }}>
                  {uploadProgress < 100
                    ? `กำลังอัปโหลดรูป... ${uploadProgress}%`
                    : 'กำลังบันทึกรายการ...'}
                </Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button variant="outlined" onClick={handleClose} disabled={isSubmitting}>
                ยกเลิก
              </Button>
              <Button
                variant="contained"
                type="submit"
                disabled={isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {isSubmitting
                  ? addedImage?.length > 0
                    ? `กำลังอัปโหลด... ${uploadProgress}%`
                    : 'กำลังบันทึก...'
                  : 'บันทึก'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );

  const renderTable = () => (
    <ExpensesTable
      expenses={expenses}
      page={page}
      size={size}
      setPage={setPage}
      setSize={setSize}
      handleEdit={handleEdit}
      handleDelete={handleDelete}
      filter={filter}
    />
  );

  // ครึ่งเดือนที่ต้องหัก CRL = ครึ่งที่ "วันที่กดเบิก (วันนี้)" ตกอยู่ — วันที่ ≤ 15 → งวด 1, ≥ 16 → งวด 2
  // (ใช้แค่แสดงในข้อความ "รายการ" — งวดที่บันทึกจริง backend คำนวณเองด้วยสูตรเดียวกันตามเวลาเซิร์ฟเวอร์)
  const getCrlDailyHalf = () => (dayjs().date() <= 15 ? 1 : 2);

  const renderAddButton = () => (
    <div className="flex  justify-between ">
      <div className="w-2/3">
        <FilterPersornalExpenses
          project={projects}
          setFindProject={setFindProject}
          setSearch={setSearch}
        />
      </div>
      <div className="flex py-1">
        <Button
          variant="contained"
          size="small"
          onClick={handleOpen}
        >
          {crlOnly ? 'สร้างรายการเบิก CRL' : 'Add Expenses'}
        </Button>
      </div>
    </div>
  );

  if (!expenses.rows && expenses.isLoading) {
    return <Loading isLoading />;
  }
  return (
    <div>
      {renderAddButton()}
      {renderTable()}
      {renderModal()}
      {renderCustomerModal()}
      <EditExpensesModal
        open={editModalOpen}
        expenseId={editExpenseId}
        onClose={() => {
          setEditModalOpen(false);
          setEditExpenseId(null);
        }}
        onSaved={fetchExpensesPage}
      />
    </div>
  );
}
